/**
 * The notes of a task or an approval as a conversation (0.99.1), and the box that adds one. Each note is a message:
 * an initials avatar, the author line, and the text in a bubble; your own notes are on the right. Decisions with a
 * comment are bubbles with a badge; status changes and decisions without one are centred one-line events.
 */
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Send } from 'lucide-react';
import { dateTime, relative } from '../lib/format';

export interface ThreadMessage {
  id: string;
  author: string;
  /** Written on a share link's page. */
  external?: boolean;
  mine?: boolean;
  createdAt: string;
  /** Plain text: nothing anyone writes is rendered as HTML. Null for an event without text. */
  body: string | null;
  /** A centred one-line event (a status change, a decision without a comment). */
  event?: boolean;
  /** A decision's badge inside the bubble (or before the event's text). */
  badge?: { tone: 'ready' | 'danger'; icon?: ReactNode; label: string };
  /** What the author did, after the name: "asked for approval". */
  verb?: string;
  /** Extra pieces in the author line, such as the timecode pill. */
  meta?: ReactNode;
  testId: string;
}

export function initials(name: string): string {
  const parts = name.replace(/[()]/g, '').trim().split(/\s+/).filter(Boolean);
  const s = parts.length > 1 ? `${parts[0]![0]}${parts[parts.length - 1]![0]}` : (parts[0] ?? '?').slice(0, 2);
  return s.toUpperCase();
}

/** Messages oldest first; "No notes yet" once loaded and empty. Scrolls inside itself and opens at the newest. */
export function Thread({ messages, loaded, empty = 'No notes yet.' }: { messages: ThreadMessage[]; loaded: boolean; empty?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const count = messages.length;
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count]);
  return (
    <div className="thread" ref={ref} data-testid="thread">
      {messages.map((m) => m.event ? (
        <div key={m.id} className="thread-event" data-testid={m.testId} data-kind="event">
          {m.badge && <span className={`thread-badge ${m.badge.tone}`}>{m.badge.icon}{m.badge.label}</span>}
          <span className="thread-event-text">
            <b>{m.author}</b>{m.external && <span className="thread-tag">via link</span>}
            {m.body && <> · <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.body}</span></>}
            {' · '}<span title={dateTime(m.createdAt)}>{relative(m.createdAt)}</span>
          </span>
          {m.meta}
        </div>
      ) : (
        <div key={m.id} className={`thread-msg ${m.mine ? 'mine' : ''}`} data-testid={m.testId} data-kind={m.badge ? 'decision' : 'note'}>
          <span className={`thread-avatar ${m.external ? 'external' : ''}`} aria-hidden="true">{initials(m.author)}</span>
          <div className="thread-col">
            <span className="thread-meta">
              <b>{m.author}</b>
              {m.external && <span className="thread-tag">via link</span>}
              {m.verb && <span>{m.verb}</span>}
              <span>·</span>
              <span title={dateTime(m.createdAt)}>{relative(m.createdAt)}</span>
              {m.meta}
            </span>
            <div className={`thread-bubble ${m.badge ? `decision ${m.badge.tone}` : ''}`}>
              {m.badge && <span className={`thread-badge ${m.badge.tone}`}>{m.badge.icon}{m.badge.label}</span>}
              {m.body && <span className="thread-text">{m.body}</span>}
            </div>
          </div>
        </div>
      ))}
      {loaded && count === 0 && <p className="faint" style={{ margin: 0 }}>{empty}</p>}
    </div>
  );
}

/**
 * Type a note and add it with Enter or the button. `wide` is the approval thread's layout: the row wraps, the input grows,
 * a note is at most 2000 characters and the button has an icon; `children` go between the input and the button.
 */
export function NoteComposer({ value, onChange, onAdd, inputTestId, addTestId, wide = false, children }: {
  value: string; onChange: (v: string) => void; onAdd: () => void; inputTestId: string; addTestId?: string; wide?: boolean; children?: ReactNode;
}) {
  return (
    <div style={wide ? { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' } : { display: 'flex', gap: 6 }}>
      <input className="input" style={wide ? { flex: 1, minWidth: 180 } : { flex: 1 }} placeholder="Add a note…" maxLength={wide ? 2000 : undefined} value={value} onChange={(e) => onChange(e.target.value)} data-testid={inputTestId}
        onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) { e.preventDefault(); onAdd(); } }} />
      {children}
      <button className="btn sm" disabled={!value.trim()} onClick={onAdd} data-testid={addTestId}>{wide ? <><Send size={13} /> Add</> : 'Add'}</button>
    </div>
  );
}
